import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CategoriaCobro, CategoriaCobroPayload,
  ConceptoCobro, ConceptoCobroPayload,
  CobroAdicional, CobroAdicionalPayload, CobroAdicionalMasivoPayload,
  EstadoCobro,
} from 'types/pago.types';
import { AñoEscolar, Grado, Seccion } from 'types/usuario.types';
import { MatriculaResponse } from 'types/matricula.types';
import {
  categoriaCobroService, conceptoCobroService, cobroAdicionalService,
} from 'services/cobrosAdicionalesService';
import { matriculaService } from 'services/matriculaService';
import { catalogoService, usuarioService } from 'services/usuarioService';
import { UsuarioListItem } from 'types/usuario.types';
import { useAuth } from 'contexts/AuthContext';
import { fmtAnioEscolar } from 'utils/anioEscolar';

// ============================================================
// CobrosAdicionalesPage — Gestión de categorías, conceptos y cobros
// ============================================================

// ── Helpers visuales ─────────────────────────────────────────

const Ico = ({ d, cls = 'w-4 h-4' }: { d: string; cls?: string }) => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={cls}><path d={d} /></svg>
);

const D_PLUS    = 'M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z';
const D_EDIT    = 'M2.695 14.763l-1.262 3.154a.5.5 0 0 0 .65.65l3.155-1.262a4 4 0 0 0 1.343-.885L17.5 5.5a2.121 2.121 0 0 0-3-3L3.58 13.42a4 4 0 0 0-.885 1.343Z';
const D_X       = 'M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z';
const D_MONEY   = 'M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.798 7.45c.512-.67 1.135-1.2 1.703-1.2s1.19.53 1.703 1.2l.127.166a.75.75 0 1 0 1.197-.904l-.127-.166C12.585 5.397 11.536 4.5 10.5 4.5c-1.035 0-2.085.897-2.901 2.046-.394.544-.744 1.183-1.003 1.851A9.6 9.6 0 0 0 6.25 10c0 .61.116 1.194.346 1.703.26.668.61 1.307 1.003 1.851C8.415 14.603 9.465 15.5 10.5 15.5c1.036 0 2.085-.897 2.901-2.046a.75.75 0 1 0-1.197-.904c-.512.67-1.135 1.2-1.703 1.2s-1.19-.53-1.703-1.2c-.32-.418-.607-.915-.8-1.448A8.075 8.075 0 0 1 7.75 10c0-.517.083-1.004.248-1.102.193-.533.48-1.03.8-1.448Z';
const D_BAN     = 'M5.28 4.22a.75.75 0 0 0-1.06 1.06L8.94 10l-4.72 4.72a.75.75 0 1 0 1.06 1.06L10 11.06l4.72 4.72a.75.75 0 1 0 1.06-1.06L11.06 10l4.72-4.72a.75.75 0 0 0-1.06-1.06L10 8.94 5.28 4.22Z';
const D_USERS   = 'M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z';
const D_SEARCH  = 'M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z';
const D_CHECK   = 'M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z';

const sol = (n: number | null | undefined) =>
  n == null ? '—' : new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

const Spin = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const ESTADO_STYLE: Record<string, string> = {
  PENDIENTE: 'bg-amber-100 text-amber-700',
  PAGADO:    'bg-green-100 text-green-700',
  ANULADO:   'bg-slate-100 text-slate-500',
};

type Tab = 'categorias' | 'conceptos' | 'cobros';

// ── Modal Categoría ───────────────────────────────────────────

const ModalCategoria: React.FC<{
  item?:    CategoriaCobro;
  onSave:   (item: CategoriaCobro) => void;
  onClose:  () => void;
}> = ({ item, onSave, onClose }) => {
  const [nombre,  setNombre]  = useState(item?.nombre  ?? '');
  const [activo,  setActivo]  = useState(item?.activo  ?? true);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true); setError(null);
    try {
      let result: CategoriaCobro;
      if (item) {
        result = await categoriaCobroService.actualizar(item.idCategoria, { nombre: nombre.trim(), activo });
      } else {
        result = await categoriaCobroService.crear({ nombre: nombre.trim() });
      }
      onSave(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <p className="font-bold text-slate-800">{item ? 'Editar categoría' : 'Nueva categoría'}</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><Ico d={D_X} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">Nombre</label>
            <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej. Útiles escolares"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          {item && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={activo} onChange={e => setActivo(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
              <span className="text-sm text-slate-700">Categoría activa</span>
            </label>
          )}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{error}</p>}
          <div className="flex gap-3 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                         text-white text-sm font-semibold disabled:opacity-60">
              {saving ? <Spin /> : <Ico d={D_CHECK} />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Modal Concepto ────────────────────────────────────────────

const ModalConcepto: React.FC<{
  item?:       ConceptoCobro;
  categorias:  CategoriaCobro[];
  onSave:      (item: ConceptoCobro) => void;
  onClose:     () => void;
}> = ({ item, categorias, onSave, onClose }) => {
  const [categoriaId,    setCategoriaId]    = useState<number | ''>(item?.categoriaId ?? '');
  const [nombre,         setNombre]         = useState(item?.nombre         ?? '');
  const [descripcion,    setDescripcion]    = useState(item?.descripcion    ?? '');
  const [montoSugerido,  setMontoSugerido]  = useState(item?.montoSugerido != null ? String(item.montoSugerido) : '');
  const [activo,         setActivo]         = useState(item?.activo         ?? true);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoriaId) { setError('Selecciona una categoría'); return; }
    if (!nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true); setError(null);
    const payload: ConceptoCobroPayload & { activo: boolean } = {
      categoriaId: Number(categoriaId),
      nombre: nombre.trim(),
      descripcion: descripcion.trim() || null,
      montoSugerido: montoSugerido ? Number(montoSugerido) : null,
      activo,
    };
    try {
      let result: ConceptoCobro;
      if (item) {
        result = await conceptoCobroService.actualizar(item.idConcepto, payload);
      } else {
        result = await conceptoCobroService.crear(payload);
      }
      onSave(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al guardar');
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500";
  const Label = ({ t }: { t: string }) => (
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">{t}</label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <p className="font-bold text-slate-800">{item ? 'Editar concepto' : 'Nuevo concepto'}</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><Ico d={D_X} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">
          <div>
            <Label t="Categoría" />
            <select value={categoriaId} onChange={e => setCategoriaId(e.target.value ? Number(e.target.value) : '')}
              className={inputCls}>
              <option value="">— Seleccionar —</option>
              {categorias.filter(c => c.activo || item?.categoriaId === c.idCategoria).map(c => (
                <option key={c.idCategoria} value={c.idCategoria}>{c.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <Label t="Nombre del concepto" />
            <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej. Útiles 2026"
              className={inputCls} />
          </div>
          <div>
            <Label t="Descripción (opcional)" />
            <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={2}
              placeholder="Descripción adicional…" className={`${inputCls} resize-none`} />
          </div>
          <div>
            <Label t="Monto sugerido (S/)" />
            <input type="number" min={0} step="0.01" value={montoSugerido}
              onChange={e => setMontoSugerido(e.target.value)} placeholder="Ej. 80.00"
              className={inputCls} />
          </div>
          {item && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={activo} onChange={e => setActivo(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 text-indigo-600" />
              <span className="text-sm text-slate-700">Concepto activo</span>
            </label>
          )}
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{error}</p>}
          <div className="flex gap-3 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                         text-white text-sm font-semibold disabled:opacity-60">
              {saving ? <Spin /> : <Ico d={D_CHECK} />}
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Modal Cobro Individual ────────────────────────────────────

const ModalCobroIndividual: React.FC<{
  conceptos:      ConceptoCobro[];
  categorias:     CategoriaCobro[];
  registradoPorId: number;
  onSave:         (cobro: CobroAdicional) => void;
  onClose:        () => void;
}> = ({ conceptos, categorias, registradoPorId, onSave, onClose }) => {
  const [modo,          setModo]          = useState<'matriculado' | 'directo'>('matriculado');
  // Modo matriculado
  const [query,         setQuery]         = useState('');
  const [buscando,      setBuscando]      = useState(false);
  const [resultados,    setResultados]    = useState<UsuarioListItem[]>([]);
  const [estudiante,    setEstudiante]    = useState<UsuarioListItem | null>(null);
  // Modo directo
  const [comprNombre,   setComprNombre]   = useState('');
  const [comprDni,      setComprDni]      = useState('');
  // Campos comunes
  const [catFiltro,     setCatFiltro]     = useState<number | ''>('');
  const [conceptoId,    setConceptoId]    = useState<number | ''>('');
  const [monto,         setMonto]         = useState('');
  const [fechaVenc,     setFechaVenc]     = useState('');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState<string | null>(null);

  const conceptosFiltrados = conceptos.filter(c =>
    c.activo && (!catFiltro || c.categoriaId === Number(catFiltro))
  );

  const buscar = useCallback(async () => {
    if (!query.trim()) return;
    setBuscando(true);
    try {
      const data = await usuarioService.listarEstudiantes({ search: query });
      setResultados(Array.isArray(data) ? data : []);
    } catch { setResultados([]); }
    finally { setBuscando(false); }
  }, [query]);

  const handleConceptoChange = (id: string) => {
    setConceptoId(id ? Number(id) : '');
    if (id) {
      const concepto = conceptos.find(c => c.idConcepto === Number(id));
      if (concepto?.montoSugerido != null) setMonto(String(concepto.montoSugerido));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conceptoId) { setError('Selecciona un concepto'); return; }
    if (!monto || Number(monto) <= 0) { setError('Ingresa un monto válido'); return; }
    if (modo === 'matriculado' && !estudiante) { setError('Selecciona un estudiante'); return; }
    if (modo === 'directo' && !comprNombre.trim()) { setError('Ingresa el nombre del comprador'); return; }

    setSaving(true); setError(null);
    const payload: CobroAdicionalPayload = {
      conceptoId: Number(conceptoId),
      monto: Number(monto),
      registradoPorId,
      fechaVencimiento: fechaVenc || undefined,
    };
    if (modo === 'matriculado' && estudiante) {
      // Necesitamos matriculaId del estudiante — usaremos idUsuario por ahora;
      // CobrosPage puede hacer lookup adicional si necesita matriculaId exacto.
      // El payload acepta matriculaId, pero la API podría aceptar estudianteId.
      // Usaremos compradorNombre/Dni como fallback si no hay matriculaId disponible.
      payload.compradorNombre = `${estudiante.apellidos}, ${estudiante.nombres}`;
      payload.compradorDni    = estudiante.dni;
    } else {
      payload.compradorNombre = comprNombre.trim();
      payload.compradorDni    = comprDni.trim() || undefined;
    }

    try {
      const result = await cobroAdicionalService.crear(payload);
      onSave(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al registrar cobro');
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500";
  const Label = ({ t }: { t: string }) => (
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">{t}</label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <p className="font-bold text-slate-800">Nuevo cobro individual</p>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><Ico d={D_X} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-4">

          {/* Tipo de cobro */}
          <div>
            <Label t="Tipo" />
            <div className="flex gap-3">
              {(['matriculado', 'directo'] as const).map(m => (
                <button key={m} type="button" onClick={() => setModo(m)}
                  className={`flex-1 py-2 rounded-xl border text-sm font-semibold transition-colors
                    ${modo === m
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'}`}>
                  {m === 'matriculado' ? 'Estudiante matriculado' : 'Venta directa'}
                </button>
              ))}
            </div>
          </div>

          {/* Identificación */}
          {modo === 'matriculado' ? (
            <div>
              <Label t="Buscar estudiante" />
              <div className="flex gap-2 mb-2">
                <div className="relative flex-1">
                  <Ico d={D_SEARCH} cls="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input value={query} onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), buscar())}
                    placeholder="Nombre o DNI…" className={`${inputCls} pl-9`} />
                </div>
                <button type="button" onClick={buscar} disabled={buscando}
                  className="px-3 py-2 rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 text-sm disabled:opacity-60">
                  {buscando ? <Spin /> : 'Buscar'}
                </button>
              </div>
              {estudiante && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-50 border border-indigo-200">
                  <span className="text-sm font-semibold text-indigo-800 flex-1">
                    {estudiante.apellidos}, {estudiante.nombres} · {estudiante.dni}
                  </span>
                  <button type="button" onClick={() => setEstudiante(null)}
                    className="text-indigo-400 hover:text-indigo-600"><Ico d={D_X} cls="w-3.5 h-3.5" /></button>
                </div>
              )}
              {resultados.length > 0 && !estudiante && (
                <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                  {resultados.slice(0, 6).map(u => (
                    <button key={u.idUsuario} type="button" onClick={() => { setEstudiante(u); setResultados([]); setQuery(''); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left">
                      <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 font-bold text-xs
                                      flex items-center justify-center flex-shrink-0">
                        {u.nombres[0]}{u.apellidos[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-700 text-sm truncate">{u.apellidos}, {u.nombres}</p>
                        <p className="text-xs text-slate-400">DNI: {u.dni}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label t="Nombre del comprador" />
                <input value={comprNombre} onChange={e => setComprNombre(e.target.value)} placeholder="Juan Pérez"
                  className={inputCls} />
              </div>
              <div>
                <Label t="DNI (opcional)" />
                <input value={comprDni} onChange={e => setComprDni(e.target.value)} placeholder="12345678"
                  className={inputCls} />
              </div>
            </div>
          )}

          {/* Concepto */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label t="Categoría" />
              <select value={catFiltro} onChange={e => setCatFiltro(e.target.value ? Number(e.target.value) : '')}
                className={inputCls}>
                <option value="">Todas las categorías</option>
                {categorias.filter(c => c.activo).map(c => (
                  <option key={c.idCategoria} value={c.idCategoria}>{c.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <Label t="Concepto" />
              <select value={conceptoId} onChange={e => handleConceptoChange(e.target.value)}
                className={inputCls}>
                <option value="">— Seleccionar —</option>
                {conceptosFiltrados.map(c => (
                  <option key={c.idConcepto} value={c.idConcepto}>
                    {c.nombre}{c.montoSugerido != null ? ` (S/ ${c.montoSugerido})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Monto y vencimiento */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label t="Monto (S/)" />
              <input type="number" min={0.01} step="0.01" value={monto}
                onChange={e => setMonto(e.target.value)} placeholder="80.00"
                className={inputCls} />
            </div>
            <div>
              <Label t="Fecha vencimiento (opcional)" />
              <input type="date" value={fechaVenc} onChange={e => setFechaVenc(e.target.value)}
                className={inputCls} />
            </div>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{error}</p>}
          <div className="flex gap-3 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
              Cancelar
            </button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                         text-white text-sm font-semibold disabled:opacity-60">
              {saving ? <Spin /> : <Ico d={D_CHECK} />}
              Registrar cobro
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Modal Cobro Masivo ────────────────────────────────────────

const ModalCobroMasivo: React.FC<{
  conceptos:       ConceptoCobro[];
  categorias:      CategoriaCobro[];
  registradoPorId: number;
  onSave:          (cobros: CobroAdicional[]) => void;
  onClose:         () => void;
}> = ({ conceptos, categorias, registradoPorId, onSave, onClose }) => {
  const [años,          setAños]          = useState<AñoEscolar[]>([]);
  const [grados,        setGrados]        = useState<Grado[]>([]);
  const [secciones,     setSecciones]     = useState<Seccion[]>([]);
  const [anioId,        setAnioId]        = useState<number | ''>('');
  const [gradoId,       setGradoId]       = useState<number | ''>('');
  const [seccionId,     setSeccionId]     = useState<number | ''>('');
  const [matriculas,    setMatriculas]    = useState<MatriculaResponse[]>([]);
  const [loadingMat,    setLoadingMat]    = useState(false);
  const [selIds,        setSelIds]        = useState<Set<number>>(new Set());

  const [catFiltro,     setCatFiltro]     = useState<number | ''>('');
  const [conceptoId,    setConceptoId]    = useState<number | ''>('');
  const [monto,         setMonto]         = useState('');
  const [fechaVenc,     setFechaVenc]     = useState('');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      catalogoService.getAñosEscolares(),
      catalogoService.getGrados(),
      catalogoService.getSecciones(),
    ]).then(([a, g, s]) => {
      const añosList = Array.isArray(a) ? a : [];
      setAños(añosList.filter(x => x.activo));
      setGrados(Array.isArray(g) ? g.filter(x => x.activo) : []);
      setSecciones(Array.isArray(s) ? s : []);
      if (añosList.length > 0) setAnioId(añosList[0].idAnioEscolar);
    }).catch(() => {});
  }, []);

  const cargarMatriculas = useCallback(async () => {
    if (!anioId) return;
    setLoadingMat(true);
    try {
      const data = await matriculaService.listar({
        anioEscolarId: Number(anioId),
        gradoId:   gradoId   ? Number(gradoId)   : undefined,
        seccionId: seccionId ? Number(seccionId) : undefined,
      });
      const activas = data.filter(m => m.activo !== false);
      setMatriculas(activas);
      setSelIds(new Set(activas.map(m => m.idMatricula)));
    } catch { setMatriculas([]); }
    finally { setLoadingMat(false); }
  }, [anioId, gradoId, seccionId]);

  const seccionesFiltradas = gradoId
    ? secciones.filter(s => s.gradoId === Number(gradoId))
    : secciones;

  const toggleSel = (id: number) => setSelIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selIds.size === matriculas.length) {
      setSelIds(new Set());
    } else {
      setSelIds(new Set(matriculas.map(m => m.idMatricula)));
    }
  };

  const handleConceptoChange = (id: string) => {
    setConceptoId(id ? Number(id) : '');
    if (id) {
      const c = conceptos.find(x => x.idConcepto === Number(id));
      if (c?.montoSugerido != null) setMonto(String(c.montoSugerido));
    }
  };

  const conceptosFiltrados = conceptos.filter(c =>
    c.activo && (!catFiltro || c.categoriaId === Number(catFiltro))
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conceptoId)    { setError('Selecciona un concepto'); return; }
    if (!monto || Number(monto) <= 0) { setError('Ingresa un monto válido'); return; }
    if (selIds.size === 0) { setError('Selecciona al menos un estudiante'); return; }

    setSaving(true); setError(null);
    const payload: CobroAdicionalMasivoPayload = {
      matriculaIds:    Array.from(selIds),
      conceptoId:      Number(conceptoId),
      monto:           Number(monto),
      fechaVencimiento: fechaVenc || undefined,
      registradoPorId,
    };
    try {
      const result = await cobroAdicionalService.crearMasivo(payload);
      onSave(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al registrar cobros');
    } finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white";
  const Label = ({ t }: { t: string }) => (
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">{t}</label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <p className="font-bold text-slate-800">Cobro masivo</p>
            <p className="text-xs text-slate-400 mt-0.5">Aplica un cobro a múltiples estudiantes a la vez</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><Ico d={D_X} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-5 space-y-5">

          {/* Filtros de estudiantes */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Seleccionar estudiantes</p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label t="Año escolar" />
                <select value={anioId} onChange={e => { setAnioId(e.target.value ? Number(e.target.value) : ''); setMatriculas([]); }}
                  className={inputCls}>
                  <option value="">— Año —</option>
                  {años.map(a => <option key={a.idAnioEscolar} value={a.idAnioEscolar}>{fmtAnioEscolar(a)}</option>)}
                </select>
              </div>
              <div>
                <Label t="Grado (opcional)" />
                <select value={gradoId} onChange={e => { setGradoId(e.target.value ? Number(e.target.value) : ''); setSeccionId(''); setMatriculas([]); }}
                  className={inputCls}>
                  <option value="">Todos los grados</option>
                  {grados.map(g => <option key={g.idGrado} value={g.idGrado}>{g.nombre} — {g.nivel}</option>)}
                </select>
              </div>
              <div>
                <Label t="Sección (opcional)" />
                <select value={seccionId} onChange={e => { setSeccionId(e.target.value ? Number(e.target.value) : ''); setMatriculas([]); }}
                  className={inputCls}>
                  <option value="">Todas las secciones</option>
                  {seccionesFiltradas.map(s => <option key={s.idSeccion} value={s.idSeccion}>{s.nombre}</option>)}
                </select>
              </div>
            </div>
            <button type="button" onClick={cargarMatriculas} disabled={!anioId || loadingMat}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200
                         text-sm font-semibold disabled:opacity-50 transition-colors">
              {loadingMat ? <Spin /> : <Ico d={D_SEARCH} />}
              {loadingMat ? 'Cargando…' : 'Buscar estudiantes'}
            </button>
          </div>

          {/* Lista de matriculas */}
          {matriculas.length > 0 && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={selIds.size === matriculas.length && matriculas.length > 0}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-slate-300 text-indigo-600" />
                  <span className="text-xs font-bold text-slate-500">
                    {selIds.size} / {matriculas.length} seleccionados
                  </span>
                </label>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                {matriculas.map(m => (
                  <label key={m.idMatricula}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={selIds.has(m.idMatricula)}
                      onChange={() => toggleSel(m.idMatricula)}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {m.estudianteApellidos}, {m.estudianteNombres}
                      </p>
                      <p className="text-xs text-slate-400">
                        {m.gradoNombre} — Sec. {m.seccionNombre}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          {matriculas.length === 0 && !loadingMat && anioId && (
            <p className="text-sm text-slate-400 text-center py-2">
              Haz clic en "Buscar estudiantes" para cargar la lista
            </p>
          )}

          {/* Concepto y monto */}
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Detalle del cobro</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label t="Categoría" />
                <select value={catFiltro} onChange={e => setCatFiltro(e.target.value ? Number(e.target.value) : '')}
                  className={inputCls}>
                  <option value="">Todas las categorías</option>
                  {categorias.filter(c => c.activo).map(c => (
                    <option key={c.idCategoria} value={c.idCategoria}>{c.nombre}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label t="Concepto" />
                <select value={conceptoId} onChange={e => handleConceptoChange(e.target.value)}
                  className={inputCls}>
                  <option value="">— Seleccionar —</option>
                  {conceptosFiltrados.map(c => (
                    <option key={c.idConcepto} value={c.idConcepto}>
                      {c.nombre}{c.montoSugerido != null ? ` (S/ ${c.montoSugerido})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label t="Monto (S/)" />
                <input type="number" min={0.01} step="0.01" value={monto}
                  onChange={e => setMonto(e.target.value)} placeholder="80.00"
                  className={inputCls} />
              </div>
              <div>
                <Label t="Fecha vencimiento (opcional)" />
                <input type="date" value={fechaVenc} onChange={e => setFechaVenc(e.target.value)}
                  className={inputCls} />
              </div>
            </div>
          </div>

          {/* Resumen */}
          {selIds.size > 0 && monto && (
            <div className="bg-indigo-50 rounded-xl px-4 py-3 flex items-center gap-3">
              <Ico d={D_USERS} cls="w-5 h-5 text-indigo-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-indigo-800">
                  Se crearán {selIds.size} cobro{selIds.size !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-indigo-600">
                  Total: {sol(selIds.size * Number(monto))} · {sol(Number(monto))} por estudiante
                </p>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{error}</p>}
          <div className="flex gap-3 justify-end pt-1">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
              Cancelar
            </button>
            <button type="submit" disabled={saving || selIds.size === 0}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                         text-white text-sm font-semibold disabled:opacity-60">
              {saving ? <Spin /> : <Ico d={D_USERS} />}
              {saving ? 'Registrando…' : `Crear ${selIds.size} cobro${selIds.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Modal Pagar Cobro ─────────────────────────────────────────

const ModalPagarCobro: React.FC<{
  cobro:           CobroAdicional;
  registradoPorId: number;
  onSave:          (cobro: CobroAdicional) => void;
  onClose:         () => void;
}> = ({ cobro, registradoPorId, onSave, onClose }) => {
  const [obs,    setObs]    = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const handlePagar = async () => {
    setSaving(true); setError(null);
    try {
      const result = await cobroAdicionalService.pagar(cobro.idCobro, {
        registradoPorId,
        observacion: obs.trim() || undefined,
      });
      onSave(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al registrar pago');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Ico d={D_MONEY} cls="w-4 h-4 text-green-700" />
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm">Registrar pago</p>
              <p className="text-xs text-slate-400 truncate max-w-[200px]">{cobro.conceptoNombre}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><Ico d={D_X} /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-sm text-slate-500">Monto a cobrar</span>
            <span className="font-bold text-slate-800 tabular-nums text-base">{sol(cobro.monto)}</span>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide block mb-1">
              Observación (opcional)
            </label>
            <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2}
              placeholder="Ej. Pago en efectivo"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-green-500 resize-none" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2">{error}</p>}
        </div>
        <div className="px-5 pb-5 flex gap-3 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
          <button onClick={handlePagar} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-green-600 hover:bg-green-700
                       text-white text-sm font-semibold disabled:opacity-60">
            {saving ? <Spin /> : <Ico d={D_CHECK} />}
            Confirmar pago
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Tab Categorías ────────────────────────────────────────────

const TabCategorias: React.FC<{
  categorias:    CategoriaCobro[];
  onUpdate:      (item: CategoriaCobro) => void;
}> = ({ categorias, onUpdate }) => {
  const [modal, setModal] = useState<{ tipo: 'nueva' | 'editar'; item?: CategoriaCobro } | null>(null);

  const handleSave = (item: CategoriaCobro) => {
    onUpdate(item);
    setModal(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{categorias.length} categoría{categorias.length !== 1 ? 's' : ''}</p>
        <button onClick={() => setModal({ tipo: 'nueva' })}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                     text-white text-sm font-semibold transition-colors">
          <Ico d={D_PLUS} />
          Nueva categoría
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {categorias.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">No hay categorías registradas</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {['Nombre', 'Estado', 'Acciones'].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {categorias.map(c => (
                <tr key={c.idCategoria} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 font-semibold text-slate-800">{c.nombre}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full
                      ${c.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                      {c.activo ? 'Activa' : 'Inactiva'}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <button onClick={() => setModal({ tipo: 'editar', item: c })}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                 bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700
                                 transition-colors">
                      <Ico d={D_EDIT} cls="w-3.5 h-3.5" />
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <ModalCategoria
          item={modal.tipo === 'editar' ? modal.item : undefined}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

// ── Tab Conceptos ─────────────────────────────────────────────

const TabConceptos: React.FC<{
  conceptos:  ConceptoCobro[];
  categorias: CategoriaCobro[];
  onUpdate:   (item: ConceptoCobro) => void;
}> = ({ conceptos, categorias, onUpdate }) => {
  const [catFiltro, setCatFiltro] = useState<number | ''>('');
  const [modal, setModal] = useState<{ tipo: 'nuevo' | 'editar'; item?: ConceptoCobro } | null>(null);

  const filtrados = catFiltro
    ? conceptos.filter(c => c.categoriaId === Number(catFiltro))
    : conceptos;

  const handleSave = (item: ConceptoCobro) => {
    onUpdate(item);
    setModal(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3">
          <select value={catFiltro} onChange={e => setCatFiltro(e.target.value ? Number(e.target.value) : '')}
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
            <option value="">Todas las categorías</option>
            {categorias.map(c => <option key={c.idCategoria} value={c.idCategoria}>{c.nombre}</option>)}
          </select>
          <p className="text-sm text-slate-500">{filtrados.length} concepto{filtrados.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setModal({ tipo: 'nuevo' })}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                     text-white text-sm font-semibold transition-colors">
          <Ico d={D_PLUS} />
          Nuevo concepto
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {filtrados.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">No hay conceptos registrados</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Categoría', 'Concepto', 'Descripción', 'Monto sugerido', 'Estado', 'Acciones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtrados.map(c => (
                  <tr key={c.idConcepto} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="inline-flex text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                        {c.categoriaNombre}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{c.nombre}</td>
                    <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate">
                      {c.descripcion || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 tabular-nums font-semibold text-slate-700">
                      {c.montoSugerido != null ? sol(c.montoSugerido) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full
                        ${c.activo ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${c.activo ? 'bg-green-500' : 'bg-slate-400'}`} />
                        {c.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => setModal({ tipo: 'editar', item: c })}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                   bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-700
                                   transition-colors">
                        <Ico d={D_EDIT} cls="w-3.5 h-3.5" />
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <ModalConcepto
          item={modal.tipo === 'editar' ? modal.item : undefined}
          categorias={categorias}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

// ── Tab Cobros ────────────────────────────────────────────────

const TabCobros: React.FC<{
  conceptos:       ConceptoCobro[];
  categorias:      CategoriaCobro[];
  registradoPorId: number;
}> = ({ conceptos, categorias, registradoPorId }) => {
  const [cobros,      setCobros]      = useState<CobroAdicional[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [filtroEst,   setFiltroEst]   = useState<EstadoCobro | ''>('');
  const matriculaIdRef                = useRef<number | null>(null);

  // Modals
  type ModalT =
    | { tipo: 'individual' }
    | { tipo: 'masivo' }
    | { tipo: 'pagar'; cobro: CobroAdicional }
    | { tipo: 'anular'; cobro: CobroAdicional };
  const [modal, setModal] = useState<ModalT | null>(null);
  const [anulando, setAnulando] = useState<number | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    if (matriculaIdRef.current) {
      try {
        const data = await cobroAdicionalService.listarPorMatricula(matriculaIdRef.current);
        setCobros(Array.isArray(data) ? data : []);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Error al cargar');
      } finally { setLoading(false); }
    } else {
      // Sin filtro de matrícula: mostrar lista vacía hasta que se cree algo
      setCobros([]);
      setLoading(false);
    }
  }, []);

  const filtrados = filtroEst ? cobros.filter(c => c.estado === filtroEst) : cobros;

  const handleSaveIndividual = (cobro: CobroAdicional) => {
    setCobros(prev => [cobro, ...prev]);
    setModal(null);
  };

  const handleSaveMasivo = (nuevos: CobroAdicional[]) => {
    setCobros(prev => [...nuevos, ...prev]);
    setModal(null);
  };

  const handlePagado = (cobro: CobroAdicional) => {
    setCobros(prev => prev.map(c => c.idCobro === cobro.idCobro ? cobro : c));
    setModal(null);
  };

  const handleAnular = async (cobro: CobroAdicional) => {
    setAnulando(cobro.idCobro);
    try {
      const updated = await cobroAdicionalService.anular(cobro.idCobro);
      setCobros(prev => prev.map(c => c.idCobro === updated.idCobro ? updated : c));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al anular');
    } finally { setAnulando(null); setModal(null); }
  };

  const persona = (c: CobroAdicional) => {
    if (c.estudianteApellidos) return `${c.estudianteApellidos}, ${c.estudianteNombres}`;
    if (c.compradorNombre) return c.compradorNombre;
    return '—';
  };

  return (
    <div className="space-y-4">
      {/* Acciones */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <select value={filtroEst} onChange={e => setFiltroEst(e.target.value as EstadoCobro | '')}
          className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
          <option value="">Todos los estados</option>
          <option value="PENDIENTE">Pendiente</option>
          <option value="PAGADO">Pagado</option>
          <option value="ANULADO">Anulado</option>
        </select>
        <div className="flex items-center gap-2">
          <button onClick={() => setModal({ tipo: 'masivo' })}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-200 bg-indigo-50
                       text-indigo-700 text-sm font-semibold hover:bg-indigo-100 transition-colors">
            <Ico d={D_USERS} />
            Cobro masivo
          </button>
          <button onClick={() => setModal({ tipo: 'individual' })}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-semibold transition-colors">
            <Ico d={D_PLUS} />
            Nuevo cobro
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Lista de cobros */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
            <Spin /><span className="text-sm">Cargando cobros…</span>
          </div>
        ) : filtrados.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">
            {cobros.length === 0
              ? 'Usa los botones de arriba para registrar cobros adicionales'
              : 'No hay cobros con ese estado'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Persona', 'Concepto', 'Categoría', 'Monto', 'Estado', 'Vencimiento', 'Acciones'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtrados.map(c => (
                  <tr key={c.idCobro} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800 leading-tight whitespace-nowrap">{persona(c)}</p>
                      {c.estudianteDni && <p className="text-xs text-slate-400">DNI: {c.estudianteDni}</p>}
                      {c.compradorDni  && !c.estudianteDni && <p className="text-xs text-slate-400">DNI: {c.compradorDni}</p>}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700">{c.conceptoNombre}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                        {c.categoriaNombre}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-800 tabular-nums whitespace-nowrap">{sol(c.monto)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap
                        ${ESTADO_STYLE[c.estado] ?? 'bg-slate-100 text-slate-500'}`}>
                        {c.estado}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                      {c.fechaVencimiento ?? <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {c.estado === 'PENDIENTE' && (
                          <>
                            <button onClick={() => setModal({ tipo: 'pagar', cobro: c })}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                         bg-green-50 text-green-700 hover:bg-green-100 border border-green-200
                                         transition-colors whitespace-nowrap">
                              <Ico d={D_MONEY} cls="w-3.5 h-3.5" />
                              Cobrar
                            </button>
                            <button onClick={() => setModal({ tipo: 'anular', cobro: c })}
                              disabled={anulando === c.idCobro}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                         bg-red-50 text-red-600 hover:bg-red-100 border border-red-200
                                         transition-colors whitespace-nowrap disabled:opacity-50">
                              {anulando === c.idCobro ? <Spin /> : <Ico d={D_BAN} cls="w-3.5 h-3.5" />}
                              Anular
                            </button>
                          </>
                        )}
                        {c.estado === 'PAGADO' && (
                          <span className="text-xs text-green-600 font-semibold">{c.fechaPago}</span>
                        )}
                        {c.estado === 'ANULADO' && (
                          <span className="text-xs text-slate-400">Anulado</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirm anular */}
      {modal?.tipo === 'anular' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5 space-y-4">
            <p className="font-bold text-slate-800">¿Anular este cobro?</p>
            <p className="text-sm text-slate-500">
              <strong>{modal.cobro.conceptoNombre}</strong> — {sol(modal.cobro.monto)}
              {modal.cobro.estudianteApellidos && ` · ${modal.cobro.estudianteApellidos}, ${modal.cobro.estudianteNombres}`}
            </p>
            <p className="text-xs text-amber-600 bg-amber-50 rounded-xl px-4 py-2">
              Esta acción no se puede deshacer.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setModal(null)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">
                Cancelar
              </button>
              <button onClick={() => handleAnular(modal.cobro)}
                disabled={anulando !== null}
                className="flex items-center gap-2 px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700
                           text-white text-sm font-semibold disabled:opacity-60">
                {anulando !== null ? <Spin /> : <Ico d={D_BAN} />}
                Anular cobro
              </button>
            </div>
          </div>
        </div>
      )}

      {modal?.tipo === 'individual' && (
        <ModalCobroIndividual
          conceptos={conceptos}
          categorias={categorias}
          registradoPorId={registradoPorId}
          onSave={handleSaveIndividual}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.tipo === 'masivo' && (
        <ModalCobroMasivo
          conceptos={conceptos}
          categorias={categorias}
          registradoPorId={registradoPorId}
          onSave={handleSaveMasivo}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.tipo === 'pagar' && (
        <ModalPagarCobro
          cobro={modal.cobro}
          registradoPorId={registradoPorId}
          onSave={handlePagado}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

// ── Componente principal ──────────────────────────────────────

const CobrosAdicionalesPage: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab]           = useState<Tab>('categorias');
  const [categorias, setCategorias] = useState<CategoriaCobro[]>([]);
  const [conceptos,  setConceptos]  = useState<ConceptoCobro[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      categoriaCobroService.listar(),
      conceptoCobroService.listar(),
    ]).then(([cats, cons]) => {
      setCategorias(Array.isArray(cats) ? cats : []);
      setConceptos(Array.isArray(cons) ? cons : []);
    }).catch(e => setError(e instanceof Error ? e.message : 'Error al cargar datos'))
      .finally(() => setLoading(false));
  }, []);

  const handleCategoriaUpdate = (item: CategoriaCobro) => {
    setCategorias(prev => {
      const idx = prev.findIndex(c => c.idCategoria === item.idCategoria);
      if (idx >= 0) { const next = [...prev]; next[idx] = item; return next; }
      return [item, ...prev];
    });
  };

  const handleConceptoUpdate = (item: ConceptoCobro) => {
    setConceptos(prev => {
      const idx = prev.findIndex(c => c.idConcepto === item.idConcepto);
      if (idx >= 0) { const next = [...prev]; next[idx] = item; return next; }
      return [item, ...prev];
    });
  };

  const TABS: { key: Tab; label: string }[] = [
    { key: 'categorias', label: 'Categorías' },
    { key: 'conceptos',  label: 'Conceptos' },
    { key: 'cobros',     label: 'Cobros' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex items-center gap-3 text-slate-400">
          <Spin />
          <span className="text-sm">Cargando módulo…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* Cabecera */}
      <div>
        <h1 className="text-xl font-bold text-slate-800">Cobros Adicionales</h1>
        <p className="text-slate-400 text-sm mt-0.5">
          Gestiona categorías, conceptos y cobros extras fuera de pensiones regulares
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px
              ${tab === t.key
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-400 hover:text-slate-600'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenido */}
      {tab === 'categorias' && (
        <TabCategorias categorias={categorias} onUpdate={handleCategoriaUpdate} />
      )}
      {tab === 'conceptos' && (
        <TabConceptos conceptos={conceptos} categorias={categorias} onUpdate={handleConceptoUpdate} />
      )}
      {tab === 'cobros' && (
        <TabCobros
          conceptos={conceptos}
          categorias={categorias}
          registradoPorId={user?.idUsuario ?? 0}
        />
      )}
    </div>
  );
};

export default CobrosAdicionalesPage;
